/**
 * Reads and writes a field that lives on a direct property of a resource,
 * placed by its `path` — e.g. the phone at `telecom[system=phone].value`.
 * One walker serves both directions, so a written value always reads back
 * from the same place.
 *
 * A write changes only that value: other array items, other parts of an
 * object and unknown elements stay as stored. FHIR has no empty strings,
 * objects or arrays, so clearing a value also removes what it leaves empty.
 */
import type { Resource } from "@medplum/fhirtypes";

import type { ConfigSchema } from "@/types/clinic-configuration";
import type { WeekSchedule } from "@/types/scheduling";
import { buildDurationQuantity } from "@/lib/utils/helpers/clinic-configuration";
import { isArray, isNumber, isObject } from "@/lib/utils/typeof";

import { readFieldValue } from "./configuration-schema-runtime";
import {
  parseHoursOfOperation,
  serializeHoursOfOperation,
} from "./hours-of-operation";

/**
 * A converter owns a whole element the walker cannot place value by value.
 */
const PROPERTY_CONVERTERS: Record<
  ConfigSchema.PropertyConverter,
  {
    parse: (stored: unknown) => WeekSchedule;
    serialize: (schedule: WeekSchedule) => unknown;
  }
> = {
  hoursOfOperation: {
    parse: parseHoursOfOperation,
    serialize: serializeHoursOfOperation,
  },
};

const isIndex = (segment: string) => /^\d+$/.test(segment);

const matches = (item: unknown, match: Record<string, string>) =>
  isObject<Record<string, unknown>>(item) &&
  Object.entries(match).every(([key, value]) => item[key] === value);

/**
 * An item that holds nothing but its `match` values is empty: it only says
 * which item it is, not anything about it.
 */
const holdsOnlyMatch = (item: unknown, match: Record<string, string>) =>
  !isObject<Record<string, unknown>>(item) ||
  Object.keys(item).every((key) => key in match);

const readAt = (node: unknown, segments: string[]): unknown => {
  const [segment, ...rest] = segments;

  if (segment === undefined) {
    return node;
  }

  if (isIndex(segment)) {
    return isArray(node) ? readAt(node[Number(segment)], rest) : undefined;
  }

  return isObject<Record<string, unknown>>(node)
    ? readAt(node[segment], rest)
    : undefined;
};

/**
 * Returns the new node instead of changing `node`, so the stored resource
 * is never touched and only the nodes along the path are copied.
 * `undefined` means "remove this node": an object or array left empty goes
 * with its last value, since FHIR has no empty elements.
 *
 * Removing an array item shifts the ones after it. `getResourceShape`
 * rejects a schema where two fields index different items of one array,
 * so a shift can only move items no field points at.
 */
const writeAt = (
  node: unknown,
  segments: string[],
  value: unknown
): unknown => {
  const [segment, ...rest] = segments;

  if (segment === undefined) {
    return value;
  }

  if (isIndex(segment)) {
    const items = isArray(node) ? [...node] : [];
    const index = Number(segment);

    /**
     * FHIR arrays have no holes, so an index past the end has no item to
     * write. That is a schema bug, not a value to place somewhere else.
     */
    if (index > items.length) {
      throw new Error(
        `Path segment "${segment}" is past the end of an array of ${items.length}.`
      );
    }

    const item = writeAt(items[index], rest, value);
    if (item === undefined) {
      items.splice(index, 1);
    } else {
      items[index] = item;
    }

    return items.length > 0 ? items : undefined;
  }

  if (isArray(node)) {
    throw new Error(
      `Path segment "${segment}" reaches an array with no index.`
    );
  }

  const object = isObject<Record<string, unknown>>(node) ? { ...node } : {};
  const child = writeAt(object[segment], rest, value);

  if (child === undefined) {
    delete object[segment];
  } else {
    object[segment] = child;
  }

  return Object.keys(object).length > 0 ? object : undefined;
};

/**
 * `match` picks an item of the path's first element, which is an array:
 * `telecom.value` picks a `telecom` item, then reads its `value`. Read and
 * write apply it at the same place, so a written value always reads back.
 */
const readMatched = (
  element: unknown,
  match: Record<string, string>,
  segments: string[]
): unknown =>
  isArray(element)
    ? readAt(
        element.find((entry) => matches(entry, match)),
        segments
      )
    : undefined;

/**
 * A missing item is appended with the `match` values set, and an item left
 * with nothing but them is removed.
 */
const writeMatched = (
  element: unknown,
  match: Record<string, string>,
  segments: string[],
  value: unknown
): unknown => {
  if (element !== undefined && !isArray(element)) {
    throw new Error(
      "A `match` field needs its first path element to be an array."
    );
  }

  const items = isArray(element) ? [...element] : [];
  const index = items.findIndex((entry) => matches(entry, match));
  const item = writeAt(
    index === -1 ? { ...match } : items[index],
    segments,
    value
  );
  const isEmpty = holdsOnlyMatch(item, match);

  if (index === -1) {
    if (!isEmpty) {
      items.push(item);
    }
  } else if (isEmpty) {
    items.splice(index, 1);
  } else {
    items[index] = item;
  }

  return items.length > 0 ? items : undefined;
};

/**
 * A `match` picks an item, so its value has to sit below the item:
 * `telecom.value`, not `telecom`. With nothing below, the write would
 * replace the item with a bare value and then drop it.
 */
const splitPath = ({ path, match }: ConfigSchema.PropertyField) => {
  const [head, ...rest] = path.split(".");

  if (match && rest.length === 0) {
    throw new Error(
      `Path "${path}" has a \`match\` but nothing below the matched item.`
    );
  }

  return { head, rest };
};

/**
 * The model value of a direct-property field: the value at `path`,
 * kept only if its type fits the field's `kind`.
 */
export const readPropertyField = (
  resource: Resource,
  field: ConfigSchema.PropertyField
): unknown => {
  const { head, rest } = splitPath(field);
  const element = readAt(resource, [head]);
  const stored = field.match
    ? readMatched(element, field.match, rest)
    : readAt(element, rest);

  if (field.kind === "weekSchedule") {
    return PROPERTY_CONVERTERS[field.converter].parse(stored);
  }

  return readFieldValue(field, stored);
};

/**
 * The property paths that index different items of one array, e.g.
 * `name.0.family` and `name.1.family`. Clearing the first would remove
 * `name.0` and move the second into its place, so the second field would
 * then write the wrong item.
 */
export const findSharedArrayIndexes = (
  fields: ConfigSchema.PropertyField[]
): string | undefined => {
  const indexesByArray = new Map<string, Set<string>>();

  for (const { path } of fields) {
    const segments = path.split(".");
    segments.forEach((segment, position) => {
      if (!isIndex(segment)) {
        return;
      }
      const array = segments.slice(0, position).join(".");
      indexesByArray.set(
        array,
        (indexesByArray.get(array) ?? new Set()).add(segment)
      );
    });
  }

  return [...indexesByArray].find(([, indexes]) => indexes.size > 1)?.[0];
};

/**
 * A copy of `resource` with the field set to `value`. `undefined`, `""` and
 * `[]` clear it: FHIR forbids empty strings and arrays, so none is ever
 * written.
 */
export const writePropertyField = <Target extends Resource>(
  resource: Target,
  field: ConfigSchema.PropertyField,
  value: unknown
): Target => {
  const toWire = (): unknown => {
    if (value === undefined || value === "") {
      return undefined;
    }
    if (isArray(value) && value.length === 0) {
      return undefined;
    }
    if (field.kind === "weekSchedule") {
      return PROPERTY_CONVERTERS[field.converter].serialize(
        value as WeekSchedule
      );
    }
    if (field.kind === "duration" && isNumber(value)) {
      return buildDurationQuantity(Number(value));
    }
    return value;
  };

  const { head, rest } = splitPath(field);
  const copy = { ...resource } as Record<string, unknown>;
  const element = field.match
    ? writeMatched(copy[head], field.match, rest, toWire())
    : writeAt(copy[head], rest, toWire());

  if (element === undefined) {
    delete copy[head];
  } else {
    copy[head] = element;
  }

  return copy as Target;
};
